<template>
  <div class="modal-canvas-toolbar">
    <div class="modal-canvas-toolbar__item">
      <span class="modal-canvas-toolbar__title">
        {{ $t('Window') }}
      </span>
    </div>
    <div class="modal-canvas-toolbar__item">
      <CollaborativeEditable
        class="modal-canvas-toolbar__collab-edit-wrap"
        :input-name="nameEditId"
      >
        <NebulaTitleTooltip
          v-if="!windowNameIsEditing"
          position="block-start"
          :text="$t('Rename Window')"
        >
          <NebulaButton
            ref="renameButton"
            icon-left="edit"
            type="flat"
            :aria-label="$t('Rename Window')"
            :is-disabled="nameEditDisabled"
            :disabled="nameEditDisabled"
            @keydown.enter="(e) => e.preventDefault()"
            @keyup.enter="setWindowNameEditing(true)"
            @click="setWindowNameEditing(true)"
          />
        </NebulaTitleTooltip>
        <NebulaInput
          v-if="windowNameIsEditing"
          ref="nameInput"
          class="modal-canvas-toolbar__name-input"
          :maxlength="150"
          size="m"
          :placeholder="$t('Enter window name')"
          :aria-label="windowNameIsValid
            ? $t('Enter window name')
            : $t('Error: Window name already exists')"
          v-model="windowName"
          has-helper
          :status="windowNameIsValid ? undefined : 'error'"
          :is-disabled="nameEditDisabled"
          :disabled="nameEditDisabled"
          @keyup.enter.escape="saveNameInput"
          @blur="saveNameInput"
        />
      </CollaborativeEditable>
    </div>
    <div
      class="modal-canvas-toolbar__item"
      v-if="!windowNameIsEditing"
    >
      <CollaborativeEditable
        class="modal-canvas-toolbar__collab-edit-wrap"
        :input-name="sizeEditId"
      >
        <NebulaDropdown
          ref="sizeDropdown"
          class="modal-canvas-toolbar__size-dropdown"
          dropdown-id="modal-canvas-toolbar-size-dropdown"
          use-white-background
          close-on-select
          :items="windowSizeOptions"
          @dropdownOpen="setWindowSizeEditing"
        >
          <template
            #trigger="{
              toggleDropdown,
              checkArrowKeyOnTrigger,
              checkArrowKeyAfterTrigger,
              isOpen,
            }"
          >
            <NebulaTitleTooltip
              position="block-start"
              :text="$t('Window Size')"
            >
              <NebulaButton
                icon-right="maximize"
                :aria-expanded="isOpen && 'true' || 'false'"
                type="flat"
                aria-haspopup="true"
                aria-controls="modal-canvas-toolbar-size-dropdown"
                :is-disabled="sizeEditDisabled"
                :disabled="sizeEditDisabled"
                @click="toggleDropdown"
                @keydown="checkArrowKeyOnTrigger"
                @keyup="checkArrowKeyAfterTrigger"
              />
            </NebulaTitleTooltip>
          </template>
        </NebulaDropdown>
      </CollaborativeEditable>
    </div>
  </div>
</template>

<script>
import { partial } from 'lodash-es';
import { mapActions, mapState } from 'pinia';
import {
  NebulaButton,
  NebulaDropdown,
  NebulaInput,
  NebulaTitleTooltip,
} from '@discoveryedu/nebula-components';
import * as types from '@/lib/constants/store';
import { getNextTitle, getWindowTitleFormat } from '@/lib/utils/titleFormat';
import { decodeHTML } from '@/lib/utils';
import {
  MODAL_CANVAS_WIDTH,
  MODAL_CANVAS_WIDTH_SMALL,
  MODAL_CANVAS_WIDTH_MEDIUM,
  MODAL_CANVAS_WIDTH_LARGE,
  MODAL_CANVAS_WIDTH_X_LARGE,
  MODAL_CANVAS_HEIGHT_SMALL,
  MODAL_CANVAS_HEIGHT_MEDIUM,
  MODAL_CANVAS_HEIGHT_LARGE,
  MODAL_CANVAS_HEIGHT_X_LARGE,
} from '@/lib/constants';
import CollaborativeEditable from '@/components/CollaborativeEditable.vue';
import {
  useEditorStore,
} from '@/stores';

export default {
  components: {
    NebulaButton,
    NebulaDropdown,
    NebulaInput,
    NebulaTitleTooltip,
    CollaborativeEditable,
  },
  data() {
    return {
      nameEditId: null,
      sizeEditId: null,
      windowNameIsEditing: false,
      windowName: null,
    };
  },
  created() {
    // Save collab edit ids so they are available for beforeUnmount() cleanup.
    this.nameEditId = `${this.currentModalCanvasId}-window-name`;
    this.sizeEditId = `${this.currentModalCanvasId}-window-size`;
  },
  beforeUnmount() {
    // Release collaborative editing locks
    this.setWindowNameEditing(false);
    this.setWindowSizeEditing(false);
  },
  watch: {
    windowNameIsEditing() {
      if (this.windowNameIsEditing) {
        this.refreshLocalWindowName();
        this.focusRef('nameInput');
      }
    },
    modalTitle() {
      /*
        If the modal title changes in the app store while we're editing
        from an undo/redo operation, then exit edit mode.
      */
      this.endNameEdit();
    },
    sizeEditDisabled() {
      if (this.sizeEditDisabled) {
        const { sizeDropdown } = this.$refs;
        if (sizeDropdown?.dropdownOpen) {
          // Ensure the dropdown is closed if locked by another user.
          sizeDropdown.closeDropdown();
        }
      }
    },
    nameEditDisabled() {
      if (this.nameEditDisabled) {
        // Exit edit mode if name edit is locked by another user.
        this.windowNameIsEditing = false;
      }
    },
  },
  computed: {
    ...mapState(useEditorStore, [
      'currentModalCanvasId',
      'modalCanvases',
      'isLocked',
    ]),
    ...mapState(useEditorStore, {
      connectedUser: 'currentUserConnection',
    }),
    modal() {
      return this.modalCanvases.find((m) => m.id === this.currentModalCanvasId);
    },
    modalTitle() {
      return this.modal?.title;
    },
    modalWidth() {
      return this.modal?.options?.width || MODAL_CANVAS_WIDTH;
    },
    otherCanvases() {
      return this.modalCanvases.filter((c) => c.id !== this.modal?.id);
    },
    otherTitles() {
      return this.otherCanvases.map((c) => c.title || '');
    },
    otherTitlesSet() {
      return new Set(this.otherTitles.map((t) => t.toLowerCase()));
    },
    trimmedWindowName() {
      return (this.windowName || '').trim();
    },
    windowNameIsValid() {
      const trimmedLowercaseName = this.trimmedWindowName.toLowerCase();
      return !this.otherTitlesSet.has(trimmedLowercaseName);
    },
    nameEditDisabled() {
      return this.isLocked(this.nameEditId);
    },
    sizeEditDisabled() {
      return this.isLocked(this.sizeEditId);
    },
    windowSizeOptions() {
      return [
        {
          text: this.$t('Small'),
          isSelected: this.modalWidth === MODAL_CANVAS_WIDTH_SMALL,
          action: partial(this.setWindowSize, {
            width: MODAL_CANVAS_WIDTH_SMALL,
            height: MODAL_CANVAS_HEIGHT_SMALL,
          }),
        },
        {
          text: this.$t('Medium'),
          isSelected: this.modalWidth === MODAL_CANVAS_WIDTH_MEDIUM,
          action: partial(this.setWindowSize, {
            width: MODAL_CANVAS_WIDTH_MEDIUM,
            height: MODAL_CANVAS_HEIGHT_MEDIUM,
          }),
        },
        {
          text: this.$t('Large'),
          isSelected: this.modalWidth === MODAL_CANVAS_WIDTH_LARGE,
          action: partial(this.setWindowSize, {
            width: MODAL_CANVAS_WIDTH_LARGE,
            height: MODAL_CANVAS_HEIGHT_LARGE,
          }),
        },
        {
          text: this.$t('Extra Large'),
          isSelected: this.modalWidth === MODAL_CANVAS_WIDTH_X_LARGE,
          action: partial(this.setWindowSize, {
            width: MODAL_CANVAS_WIDTH_X_LARGE,
            height: MODAL_CANVAS_HEIGHT_X_LARGE,
          }),
        },
      ];
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.UPDATE_CURRENTLY_EDITING,
      types.UPDATE_MODAL_CANVAS,
      types.RESIZE_MODAL_CANVAS,
    ]),
    focusRef(refName) {
      this.$nextTick(() => {
        const ref = this.$refs?.[refName];
        const refEl = ref?.$el || ref;
        if (refEl) {
          refEl.focus();
        }
      });
    },
    endNameEdit() {
      this.windowName = null;
      this.setWindowNameEditing(false);
      this.focusRef('renameButton');
    },
    saveNameInput() {
      if (
        !this.windowNameIsEditing
        || !this.windowNameIsValid
        || this.nameEditDisabled
      ) return;
      let windowName = this.trimmedWindowName;
      if (!windowName) {
        windowName = getNextTitle(this.otherTitles, getWindowTitleFormat());
      }
      if (windowName !== this.modal?.title) {
        this[types.UPDATE_MODAL_CANVAS]({
          id: this.modal?.id,
          title: windowName,
        });
      }
      this.endNameEdit();
    },
    setWindowNameEditing(isEditing) {
      if (this.nameEditDisabled) return;
      this.windowNameIsEditing = isEditing;
      this[types.UPDATE_CURRENTLY_EDITING]({
        [this.nameEditId]: isEditing ? this.connectedUser?.id : null,
      });
    },
    setWindowSize(size) {
      this[types.RESIZE_MODAL_CANVAS]({
        modalCanvasId: this.currentModalCanvasId,
        ...size,
      });
    },
    setWindowSizeEditing(isEditing) {
      if (this.sizeEditDisabled) return;
      this[types.UPDATE_CURRENTLY_EDITING]({
        [this.sizeEditId]: isEditing ? this.connectedUser?.id : null,
      });
    },
    refreshLocalWindowName() {
      this.windowName = decodeHTML(this.modalTitle || '');
    },
  },
};
</script>

<style lang="stylus">
.modal-canvas-toolbar {
  align-items: center;
  background: $nebula-color-white;
  border-radius: $nebula-border-radius-small;
  box-shadow: 0px 2px $nebula-space-half rgba($nebula-color-black, 0.25);
  display: flex;
  font-size: $nebula-font-size-body-2;
  gap: $nebula-space-half;
  justify-content: center;
  min-height: $nebula-space-6x;
  padding: $nebula-space-half;
  padding-inline-start: $nebula-space-2x;
  width: max-content;

  &__collab-edit-wrap {
    border-radius: $nebula-space-1x;
    position: relative;
  }

  &__item {
    align-items: center;
    display: flex;

    &:first-child {
      margin-inline-end: $nebula-space-half;
    }
  }

  &__name-input {
    margin: 0;
    max-width: 225px;
    width: calc(100vw - 400px);
  }

  &__title {
    font-weight: bold;
  }

  &__size-dropdown {
    --nebula-dropdown-content-min-width: 0;

    &.nebula-dropdown--white-background {
      .nebula-dropdown__item {
        &.nebula-dropdown__item--selected {
          .nebula-dropdown-item__inner {
            &:not(.nebula-dropdown-item__inner--heading) {
              --nebula-dropdown-item-selected-text-decoration: none;
              background: $nebula-color-platform-interactive-300;
            }
          }
        }
      }
    }
  }
}
</style>
